import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, FilterBar, MetricCard, Modal, PageHeader, StatusBadge } from '../components/ui';
import {
  adminWorkflowsService,
  RecallCase,
  RecallSeverity,
  RecallStatus,
} from '../services/admin-workflows.service';

type ViewMode = 'grid' | 'list';
type SortMode = 'latest' | 'severity' | 'impacted';

interface RecallFormState {
  product: string;
  batchId: string;
  region: string;
  severity: RecallSeverity;
  impactedUnits: number;
  reason: string;
}

const emptyForm: RecallFormState = {
  product: '',
  batchId: '',
  region: '',
  severity: 'high',
  impactedUnits: 0,
  reason: '',
};

const severityOrder: Record<RecallSeverity, number> = {
  high: 0,
  medium: 1,
  low: 2,
};

const statusTone = (status: RecallStatus) => {
  if (status === 'active') return 'recalled';
  if (status === 'contained') return 'anchored';
  if (status === 'closed') return 'disabled';
  return 'muted';
};

const formatDateTime = (value: string) =>
  new Date(value).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

export default function AdminRecallManagementPage() {
  const navigate = useNavigate();
  const [recalls, setRecalls] = useState<RecallCase[]>([]);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | RecallStatus>('all');
  const [severityFilter, setSeverityFilter] = useState<'all' | RecallSeverity>('all');
  const [sortBy, setSortBy] = useState<SortMode>('latest');
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [createForm, setCreateForm] = useState<RecallFormState>(emptyForm);

  const [closeTarget, setCloseTarget] = useState<RecallCase | null>(null);
  const [closeReason, setCloseReason] = useState('');
  const [closeError, setCloseError] = useState<string | null>(null);

  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);

  useEffect(() => {
    let active = true;

    const loadRecalls = async () => {
      try {
        setLoading(true);
        const data = await adminWorkflowsService.getRecallCases({
          sortBy: 'latest',
        });

        if (!active) {
          return;
        }

        setRecalls(data);
        setError(null);
      } catch (loadError) {
        if (!active) {
          return;
        }
        setError((loadError as Error).message || 'Failed to load recall cases');
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };

    loadRecalls();

    return () => {
      active = false;
    };
  }, []);

  const statusOptions = useMemo(
    () => [
      { value: 'all', label: 'All', count: recalls.length },
      { value: 'draft', label: 'Draft', count: recalls.filter((item) => item.status === 'draft').length },
      { value: 'active', label: 'Active', count: recalls.filter((item) => item.status === 'active').length },
      { value: 'contained', label: 'Contained', count: recalls.filter((item) => item.status === 'contained').length },
      { value: 'closed', label: 'Closed', count: recalls.filter((item) => item.status === 'closed').length },
    ],
    [recalls]
  );

  const visibleRecalls = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();
    const filtered = recalls.filter((item) => {
      const matchesStatus = statusFilter === 'all' || item.status === statusFilter;
      const matchesSeverity = severityFilter === 'all' || item.severity === severityFilter;
      const matchesQuery =
        normalizedQuery === '' ||
        item.productName.toLowerCase().includes(normalizedQuery) ||
        item.batchId.toLowerCase().includes(normalizedQuery) ||
        item.region.toLowerCase().includes(normalizedQuery);
      return matchesStatus && matchesSeverity && matchesQuery;
    });

    return filtered.sort((a, b) => {
      if (sortBy === 'severity') {
        return severityOrder[a.severity] - severityOrder[b.severity];
      }
      if (sortBy === 'impacted') {
        return b.impactedUnits - a.impactedUnits;
      }
      return new Date(b.initiatedAt).getTime() - new Date(a.initiatedAt).getTime();
    });
  }, [query, recalls, severityFilter, sortBy, statusFilter]);

  const metricSummary = useMemo(
    () => ({
      active: recalls.filter((item) => item.status === 'active').length,
      contained: recalls.filter((item) => item.status === 'contained').length,
      highSeverity: recalls.filter((item) => item.severity === 'high').length,
      totalImpacted: recalls.reduce((sum, item) => sum + item.impactedUnits, 0),
    }),
    [recalls]
  );

  const applyRecallUpdate = (updatedRecall: RecallCase, message: string) => {
    setRecalls((prev) => prev.map((item) => (item.id === updatedRecall.id ? updatedRecall : item)));
    setSuccess(message);
    setError(null);
  };

  const transitionToContained = async (recallId: string) => {
    try {
      setActionLoading(`contain-${recallId}`);
      const updated = await adminWorkflowsService.containRecallCase(recallId);
      applyRecallUpdate(updated, 'Recall marked as contained.');
    } catch (actionError) {
      setError((actionError as Error).message || 'Failed to mark recall as contained');
    } finally {
      setActionLoading(null);
    }
  };

  const transitionToActive = async (recallId: string) => {
    try {
      setActionLoading(`activate-${recallId}`);
      const updated = await adminWorkflowsService.activateRecallCase(recallId);
      applyRecallUpdate(updated, 'Recall activated.');
    } catch (actionError) {
      setError((actionError as Error).message || 'Failed to activate recall');
    } finally {
      setActionLoading(null);
    }
  };

  const openCloseModal = (item: RecallCase) => {
    setCloseTarget(item);
    setCloseReason('');
    setCloseError(null);
  };

  const submitCloseRecall = async () => {
    if (!closeTarget) {
      return;
    }

    if (closeReason.trim().length < 10) {
      setCloseError('Closure reason is required (minimum 10 characters).');
      return;
    }

    try {
      setActionLoading(`close-${closeTarget.id}`);
      const updated = await adminWorkflowsService.closeRecallCase(closeTarget.id, closeReason.trim());
      applyRecallUpdate(updated, 'Recall closed with reason captured.');
      setCloseTarget(null);
      setCloseReason('');
      setCloseError(null);
    } catch (actionError) {
      setCloseError((actionError as Error).message || 'Failed to close recall');
    } finally {
      setActionLoading(null);
    }
  };

  const submitCreateRecall = async () => {
    if (
      createForm.product.trim() === '' ||
      createForm.batchId.trim() === '' ||
      createForm.region.trim() === '' ||
      createForm.reason.trim().length < 10 ||
      createForm.impactedUnits < 1
    ) {
      setCreateError('Provide product, batch, region, impacted units, and a reason (10+ chars).');
      return;
    }

    try {
      setActionLoading('create-recall');
      const created = await adminWorkflowsService.createRecallCase({
        product: createForm.product.trim(),
        batchId: createForm.batchId.trim(),
        region: createForm.region.trim(),
        severity: createForm.severity,
        impactedUnits: createForm.impactedUnits,
        reason: createForm.reason.trim(),
      });

      setRecalls((prev) => [created, ...prev]);
      setShowCreateModal(false);
      setCreateForm(emptyForm);
      setCreateError(null);
      setSuccess('Recall initiated with mandatory reason captured.');
      setError(null);
    } catch (actionError) {
      setCreateError((actionError as Error).message || 'Failed to initiate recall');
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
      <PageHeader
        title="Recall Management"
        description="Critical-path recall operations with lifecycle transitions and mandatory reason capture."
        badge={<StatusBadge tone="recalled">Critical Workflow</StatusBadge>}
        actions={
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate('/admin')}>
              Back
            </Button>
            <Button onClick={() => setShowCreateModal(true)}>Initiate Recall</Button>
          </div>
        }
      />

      <div className="rounded-xl border border-red-200 bg-red-50 p-4">
        <p className="text-sm text-red-800">
          Recall actions are immutable operations. Reason capture is mandatory for initiation and closure.
        </p>
      </div>

      {success && <Alert variant="success">{success}</Alert>}
      {error && <Alert variant="error">{error}</Alert>}

      <section className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        <MetricCard title="Active Recalls" value={metricSummary.active} tone="red" icon="A" />
        <MetricCard title="Contained Recalls" value={metricSummary.contained} tone="blue" icon="C" />
        <MetricCard title="High Severity" value={metricSummary.highSeverity} tone="yellow" icon="H" />
        <MetricCard title="Impacted Units" value={metricSummary.totalImpacted.toLocaleString()} tone="purple" icon="U" />
      </section>

      <FilterBar
        searchValue={query}
        onSearchChange={setQuery}
        searchPlaceholder="Search product, batch, or region..."
        statusOptions={statusOptions}
        selectedStatus={statusFilter}
        onStatusChange={(value) => setStatusFilter(value as 'all' | RecallStatus)}
        sortOptions={[
          { value: 'latest', label: 'Sort: Latest First' },
          { value: 'severity', label: 'Sort: Severity' },
          { value: 'impacted', label: 'Sort: Impacted Units' },
        ]}
        selectedSort={sortBy}
        onSortChange={(value) => setSortBy(value as SortMode)}
        viewOptions={[
          { value: 'grid', label: 'View: Grid' },
          { value: 'list', label: 'View: List' },
        ]}
        selectedView={viewMode}
        onViewChange={(value) => setViewMode(value as ViewMode)}
      />

      <section className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
        <label className="text-sm font-medium text-gray-700">
          Severity Filter
          <select
            value={severityFilter}
            onChange={(event) => setSeverityFilter(event.target.value as 'all' | RecallSeverity)}
            className="mt-2 w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-900 outline-none transition focus:border-emerald-300 focus:ring-2 focus:ring-emerald-100 md:max-w-xs"
          >
            <option value="all">All severities</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
        </label>
      </section>

      {loading ? (
        <section className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm text-sm text-gray-600">Loading recall cases...</section>
      ) : viewMode === 'grid' ? (
        <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {visibleRecalls.map((item) => (
            <article key={item.id} className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm space-y-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h2 className="text-base font-semibold text-gray-900">{item.productName}</h2>
                <StatusBadge tone={statusTone(item.status)} size="sm">
                  {item.status}
                </StatusBadge>
              </div>
              <dl className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <dt className="text-xs font-semibold uppercase text-gray-500">Batch</dt>
                  <dd className="text-gray-900">{item.batchId}</dd>
                </div>
                <div>
                  <dt className="text-xs font-semibold uppercase text-gray-500">Region</dt>
                  <dd className="text-gray-900">{item.region}</dd>
                </div>
                <div>
                  <dt className="text-xs font-semibold uppercase text-gray-500">Severity</dt>
                  <dd className="text-gray-900 capitalize">{item.severity}</dd>
                </div>
                <div>
                  <dt className="text-xs font-semibold uppercase text-gray-500">Impacted Units</dt>
                  <dd className="text-gray-900">{item.impactedUnits.toLocaleString()}</dd>
                </div>
                <div className="col-span-2">
                  <dt className="text-xs font-semibold uppercase text-gray-500">Initiated</dt>
                  <dd className="text-gray-900">{formatDateTime(item.initiatedAt)}</dd>
                </div>
              </dl>
              <p className="text-sm text-gray-600">{item.reason}</p>
              <div className="flex flex-wrap gap-2">
                {item.status === 'draft' && (
                  <Button size="sm" onClick={() => transitionToActive(item.id)} disabled={actionLoading !== null}>
                    Activate
                  </Button>
                )}
                {item.status === 'active' && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => transitionToContained(item.id)}
                    disabled={actionLoading !== null}
                  >
                    Mark Contained
                  </Button>
                )}
                {(item.status === 'active' || item.status === 'contained') && (
                  <Button
                    size="sm"
                    variant="danger"
                    onClick={() => openCloseModal(item)}
                    disabled={actionLoading !== null}
                  >
                    Close Recall
                  </Button>
                )}
              </div>
            </article>
          ))}
        </section>
      ) : (
        <section className="rounded-xl border border-gray-200 bg-white shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200" aria-label="Recall cases">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Product</th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Batch</th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Region</th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Status</th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Severity</th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Impacted</th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-gray-500">Initiated</th>
                  <th scope="col" className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wider text-gray-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {visibleRecalls.map((item) => (
                  <tr key={item.id}>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">{item.productName}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{item.batchId}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{item.region}</td>
                    <td className="px-4 py-3">
                      <StatusBadge tone={statusTone(item.status)} size="sm">
                        {item.status}
                      </StatusBadge>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-700 capitalize">{item.severity}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{item.impactedUnits.toLocaleString()}</td>
                    <td className="px-4 py-3 text-sm text-gray-700">{formatDateTime(item.initiatedAt)}</td>
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-2">
                        {item.status === 'draft' && (
                          <Button size="sm" onClick={() => transitionToActive(item.id)} disabled={actionLoading !== null}>
                            Activate
                          </Button>
                        )}
                        {item.status === 'active' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => transitionToContained(item.id)}
                            disabled={actionLoading !== null}
                          >
                            Contain
                          </Button>
                        )}
                        {(item.status === 'active' || item.status === 'contained') && (
                          <Button
                            size="sm"
                            variant="danger"
                            onClick={() => openCloseModal(item)}
                            disabled={actionLoading !== null}
                          >
                            Close
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      <Modal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          setCreateError(null);
          setCreateForm(emptyForm);
        }}
        title="Initiate Recall"
      >
        <div className="space-y-4">
          {createError && <Alert variant="error">{createError}</Alert>}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <label className="text-sm font-medium text-gray-700">
              Product
              <input
                value={createForm.product}
                onChange={(event) => setCreateForm((prev) => ({ ...prev, product: event.target.value }))}
                className="mt-2 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 outline-none transition focus:border-emerald-300 focus:ring-2 focus:ring-emerald-100"
              />
            </label>
            <label className="text-sm font-medium text-gray-700">
              Batch ID
              <input
                value={createForm.batchId}
                onChange={(event) => setCreateForm((prev) => ({ ...prev, batchId: event.target.value }))}
                className="mt-2 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 outline-none transition focus:border-emerald-300 focus:ring-2 focus:ring-emerald-100"
              />
            </label>
            <label className="text-sm font-medium text-gray-700">
              Region
              <input
                value={createForm.region}
                onChange={(event) => setCreateForm((prev) => ({ ...prev, region: event.target.value }))}
                className="mt-2 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 outline-none transition focus:border-emerald-300 focus:ring-2 focus:ring-emerald-100"
              />
            </label>
            <label className="text-sm font-medium text-gray-700">
              Severity
              <select
                value={createForm.severity}
                onChange={(event) => setCreateForm((prev) => ({ ...prev, severity: event.target.value as RecallSeverity }))}
                className="mt-2 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 outline-none transition focus:border-emerald-300 focus:ring-2 focus:ring-emerald-100"
              >
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </label>
            <label className="text-sm font-medium text-gray-700 md:col-span-2">
              Impacted Units
              <input
                type="number"
                min={1}
                value={createForm.impactedUnits}
                onChange={(event) =>
                  setCreateForm((prev) => ({ ...prev, impactedUnits: Number(event.target.value) || 0 }))
                }
                className="mt-2 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 outline-none transition focus:border-emerald-300 focus:ring-2 focus:ring-emerald-100"
              />
            </label>
          </div>

          <label className="text-sm font-medium text-gray-700 block">
            Recall reason (required)
            <textarea
              value={createForm.reason}
              onChange={(event) => setCreateForm((prev) => ({ ...prev, reason: event.target.value }))}
              rows={4}
              className="mt-2 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 outline-none transition focus:border-emerald-300 focus:ring-2 focus:ring-emerald-100"
              placeholder="Provide the reason for recall initiation..."
            />
          </label>

          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setShowCreateModal(false)}>
              Cancel
            </Button>
            <Button onClick={submitCreateRecall} disabled={actionLoading !== null}>
              Initiate Recall
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={closeTarget !== null}
        onClose={() => {
          setCloseTarget(null);
          setCloseReason('');
          setCloseError(null);
        }}
        title="Close Recall"
      >
        <div className="space-y-4">
          {closeTarget && (
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm">
              <p className="font-medium text-gray-900">{closeTarget.productName}</p>
              <p className="text-gray-600">{closeTarget.batchId} / {closeTarget.region}</p>
            </div>
          )}
          {closeError && <Alert variant="error">{closeError}</Alert>}
          <label className="text-sm font-medium text-gray-700 block">
            Closure reason (required)
            <textarea
              value={closeReason}
              onChange={(event) => setCloseReason(event.target.value)}
              rows={4}
              className="mt-2 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 outline-none transition focus:border-emerald-300 focus:ring-2 focus:ring-emerald-100"
              placeholder="Describe closure conditions and verification..."
            />
          </label>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setCloseTarget(null)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={submitCloseRecall} disabled={actionLoading !== null}>
              Confirm Close
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
